import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Avatar from '@mui/material/Avatar';
import { teal, lightBlue } from '@mui/material/colors';
import CardHeader from '@mui/material/CardHeader';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useChatMutation } from '../store/services/agentAssistantService';
import { setAgentAssistant } from '../store/features/agentAssistantSlice';
import SpeechButton from '../components/common/SpeechButton';
import { useTextToSpeech } from '../hooks/useTextToSpeech';
import ContextTable from '../components/chatbot/ContextTable';

interface Message {
  content: string;
  agent: string;
  context?: ContextItem[];
}

interface ContextItem {
  grower_id: string;
  job_id: string;
  job_operator: string | null;
  job_type: string;
  crop_type: string;
  job_name: string;
  field_name: string;
  note: string | null;
  is_deleted: boolean;
}

interface AgentAssistantResponse {
  conversation_id: string;
  current_agent: string;
  messages: Message[];
  events: any[];
  context: ContextItem[];
  guardrails: any[];
}

const AgentAssistantPage = () => {
  const dispatch = useDispatch();
  const agentAssistant = useSelector((state: any) => state.agentAssistant.agentAssistant);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [clearTranscriptFn, setClearTranscriptFn] = useState<(() => void) | null>(null);
  const [chat, { isLoading }] = useChatMutation();
  const [ttsEnabled, setTtsEnabled] = useState<boolean>(true);
  const [currentContext, setCurrentContext] = useState<ContextItem[]>([]);

  // Text-to-speech hook
  const { speak, isSpeaking, stop } = useTextToSpeech({
    voice: 'en-US-JennyNeural',
    onError: (error) => console.error('TTS Error:', error)
  });

  useEffect(() => {
    const chatContainer = document.getElementById('agent-chat-container');
    if (chatContainer) {
      chatContainer.scrollTo({
        top: chatContainer.scrollHeight - chatContainer.clientHeight,
        behavior: 'smooth'
      });
    }

    const contextTableContainer = document.getElementById('context-table-container');
    if (contextTableContainer) {
      contextTableContainer.scrollTo({
        top: contextTableContainer.scrollHeight - contextTableContainer.clientHeight,
        behavior: 'smooth'
      });
    }
  }, [messages]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    
    // Clear transcript when user manually clears the input
    if (!newValue && clearTranscriptFn) {
      clearTranscriptFn();
    }
  };

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!inputValue.trim()) return;

      const userMessage: Message = {
        content: inputValue,
        agent: 'User'
      };

      // Add user message to the conversation
      setMessages(prev => [...prev, userMessage]);

      try {
        const response = await chat({
          conversation_id: conversationId,
          message: inputValue
        }).unwrap() as AgentAssistantResponse;

        // Update conversation ID if this is the first message
        if (!conversationId && response.conversation_id) {
          setConversationId(response.conversation_id);
        }

        // Add agent messages to the conversation with context
        if (response.messages && response.messages.length > 0) {
          const messagesWithContext = response.messages.map(message => ({
            ...message,
            context: response.context || []
          }));
          setMessages(prev => [...prev, ...messagesWithContext]);

          // Speak the latest agent message if TTS is enabled
          const latestMessage = response.messages[response.messages.length - 1];
          if (latestMessage && latestMessage.agent !== 'User' && ttsEnabled) {
            speak(latestMessage.content);
          }
        }

        // Update the store
        dispatch(setAgentAssistant(response));

        // Update current context with the latest response
        if (response.context && response.context.length > 0) {
          setCurrentContext(response.context);
        }

        // Clear input and transcript
        setInputValue('');
        if (clearTranscriptFn) {
          clearTranscriptFn();
        }
      } catch (error) {
        console.error('Error sending message:', error);
        // Add error message to conversation
        setMessages(prev => [...prev, {
          content: 'Sorry, there was an error processing your request.',
          agent: 'System'
        }]);
      }
    }
  };

  const handleSpeechResult = (text: string) => {
    setInputValue(text);
  };

  const handleSpeechPartialResult = (text: string) => {
    setInputValue(text);
  };

  // Add a function to clear the input when starting speech
  const handleStartListening = () => {
    setInputValue(''); // Clear the input field
  };

  // Handle setting the clearTranscript function from SpeechButton
  const handleClearTranscript = (clearTranscript: () => void) => {
    setClearTranscriptFn(() => clearTranscript);
  };

  const handleTtsToggle = () => {
    if (isSpeaking) {
      stop();
    }
    setTtsEnabled(!ttsEnabled);
  };

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
      <Box sx={{ height: 'calc(100vh - 80px)', width: '100%', display: 'flex', flexDirection: 'column', mt: '30px', mx: 2 }}>
        {/* Main content area with chat and context tables */}
        <Box sx={{ flex: 1, display: 'flex', gap: 1, overflowY: 'hidden', overflowX: 'hidden' }}>
          {/* Chat Column */}
          <Box sx={{ flex: '0 0 50%', overflowY: 'hidden', overflowX: 'hidden' }}>
            <Paper sx={{ height: 'calc(100vh - 170px)', display: 'flex', flexDirection: 'column', overflowX: 'hidden' }}>
              <Box id="agent-chat-container" sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                <Box
                  sx={{
                    backgroundColor: '#f5f5f5',
                    color: '#000',
                    textAlign: 'left'
                  }}>
                  <CardHeader
                    avatar={
                      <Avatar sx={{ bgcolor: lightBlue['500'] }}>AI</Avatar>
                    }
                    title="Hi, I'm your Job Assistant. How can I help you today?"
                  />
                </Box>

                {messages.map((message, index) => (
                  <Box key={index}>
                    <Box
                      sx={{
                        backgroundColor: '#f5f5f5',
                        color: '#000',
                        textAlign: 'left'
                      }}>
                      <CardHeader
                        avatar={
                          <Avatar sx={{
                            bgcolor: message.agent === 'User' ? teal[100] : lightBlue['500']
                          }}>
                            {message.agent === 'User' ? 'U' : 'AI'}
                          </Avatar>
                        }
                        title={
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {message.content}
                            {message.agent !== 'User' && isSpeaking && (
                              <VolumeUpIcon
                                sx={{
                                  fontSize: '16px',
                                  color: 'primary.main',
                                  animation: 'pulse 1.5s infinite'
                                }}
                              />
                            )}
                          </div>
                        }
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>

          {/* Context Tables Column */}
          <Box sx={{ flex: '0 0 50%', overflowY: 'hidden', overflowX: 'hidden' }}>
            <Paper sx={{ height: 'calc(100vh - 170px)', overflowY: 'auto', overflowX: 'hidden' }}>
              <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                Latest Context
              </Typography>
              {(() => {
                const latestContextMessage = messages
                  .filter(m => m.agent !== 'User' && m.context && m.context.length > 0)
                  .pop();
                
                if (latestContextMessage && latestContextMessage.context) {
                  return (
                    <Box id="context-table-container">
                      <ContextTable context={latestContextMessage.context} />
                    </Box>
                  );
                } else {
                  return (
                    <Typography variant="body2" color="text.secondary" align="center">
                      No context data available yet
                    </Typography>
                  );
                }
              })()}
            </Paper>
          </Box>
        </Box>

        {/* Input Section at the bottom */}
        <Box sx={{ p: 1, backgroundColor: '#f5f5f5', borderTop: 1, borderColor: 'divider', position: 'fixed', bottom: 0, width: '100%' }}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs>
              <TextField
                fullWidth
                label="Type your message here..."
                variant="outlined"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                autoComplete="off"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SpeechButton
                        onResult={handleSpeechResult}
                        onPartialResult={handleSpeechPartialResult}
                        onStartListening={handleStartListening}
                        onClearTranscript={handleClearTranscript}
                        size="small"
                      />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item>
              <IconButton
                onClick={handleTtsToggle}
                color={ttsEnabled ? "primary" : "default"}
                disabled={isLoading}
                title={ttsEnabled ? "Disable Text-to-Speech" : "Enable Text-to-Speech"}
              >
                {ttsEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
              </IconButton>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default AgentAssistantPage; 